import { collection, addDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import app, { db } from '../firebase/firebaseConfig';
import LevelingSystem from '../components/SRS/LevelingSystem';
import { fetchCookbookSaveQuota, fetchManualSnippetCreationQuota } from './subscriptionQuotaService';
import { confirmCookbookDeckNotRemovedByQuotaTrigger } from '../utils/cookbookDeckQuotaTriggerWatch';

const functions = getFunctions(app, 'us-central1');

function nowIso() {
  return new Date().toISOString();
}

/**
 * Client-side parse of GitHub URLs so the modal can auto-fill folder path.
 * Mirrors functions/githubImport.ts parseGitHubRepoInput (non-throwing).
 */
export function parseGitHubRepoUrlClient(input) {
  const cleaned = String(input || '')
    .trim()
    .replace(/^git\+/i, '')
    .replace(/\.git$/i, '')
    .replace(/[?#].*$/, '');
  if (!cleaned) return null;

  const deepMatch = cleaned.match(
    /(?:https?:\/\/)?(?:www\.)?github\.com\/([^/\s]+)\/([^/\s]+)(?:\/(?:tree|blob)\/([^/\s]+)(?:\/(.*))?)?/i
  );
  if (deepMatch) {
    const pathPrefix = (deepMatch[4] || '')
      .replace(/\/+$/, '')
      .replace(/^\/+/, '')
      .trim();
    return {
      owner: deepMatch[1],
      repo: deepMatch[2].replace(/\.git$/i, ''),
      ref: deepMatch[3] || null,
      pathPrefix: pathPrefix || null,
    };
  }

  const shortMatch = cleaned.match(/^([^/\s]+)\/([^/\s]+)$/);
  if (shortMatch) {
    return {
      owner: shortMatch[1],
      repo: shortMatch[2].replace(/\.git$/i, ''),
      ref: null,
      pathPrefix: null,
    };
  }

  return null;
}

/**
 * Fetch public GitHub repo files as cookbook card payloads.
 */
export async function fetchGitHubRepoForImport({ repoUrl, pathPrefix = '', maxFiles = 40 }) {
  const fn = httpsCallable(functions, 'fetchGitHubRepoForImport', { timeout: 120000 });
  const result = await fn({ repoUrl, pathPrefix, maxFiles });
  return result.data;
}

/**
 * Create a library cookbook from GitHub import card payloads.
 * Mirrors importCommunityCookbook quota + leveling behavior.
 * Accepts optional `name` for the cookbook title and edited `cards`.
 */
export async function importGitHubCookbook(userId, payload) {
  if (!userId) {
    return { ok: false, reason: 'unauthenticated' };
  }
  if (!payload?.cards?.length) {
    return { ok: false, reason: 'empty' };
  }

  const quota = await fetchCookbookSaveQuota();
  if (quota.quotaCheckUnavailable) {
    return { ok: false, reason: 'quota_unavailable', quota };
  }
  if (!quota.isPremium && quota.allowed === false) {
    return { ok: false, reason: 'quota_exceeded', quota };
  }

  const snippetQuota = await fetchManualSnippetCreationQuota();
  if (snippetQuota.quotaCheckUnavailable) {
    return { ok: false, reason: 'snippet_quota_unavailable', quota: snippetQuota };
  }
  if (!snippetQuota.isPremium) {
    if (snippetQuota.allowed === false) {
      return { ok: false, reason: 'snippet_quota_exceeded', quota: snippetQuota };
    }
    if (
      snippetQuota.remaining != null &&
      payload.cards.length > snippetQuota.remaining
    ) {
      return { ok: false, reason: 'snippet_quota_exceeded', quota: snippetQuota };
    }
  }

  const levelingSystem = new LevelingSystem(userId);
  const levelingFields = levelingSystem.initializeCookbookLeveling();
  const timestamp = nowIso();
  const fullName = payload.fullName || `${payload.owner}/${payload.repo}`;
  const cookbookName = (payload.name || payload.repo || fullName).trim() || fullName;

  const decksRef = collection(db, `users/${userId}/decks`);
  const deckRef = await addDoc(decksRef, {
    name: cookbookName,
    description: payload.description || `Imported from ${fullName}`,
    language: payload.language || 'Other',
    tags: ['github', payload.language || 'Other'].filter(Boolean),
    type: 'cookbook',
    imported: true,
    importedFrom: `github:${fullName}`,
    importedAt: timestamp,
    githubOwner: payload.owner || null,
    githubRepo: payload.repo || null,
    githubBranch: payload.defaultBranch || null,
    createdAt: timestamp,
    lastAccessed: timestamp,
    level: levelingFields.level,
  });

  if (!quota.isPremium) {
    const outcome = await confirmCookbookDeckNotRemovedByQuotaTrigger(deckRef);
    if (!outcome.ok && outcome.removedByMonthlyLimit) {
      return { ok: false, reason: 'removed_by_quota_trigger' };
    }
  }

  const userCardsRef = collection(db, `users/${userId}/decks/${deckRef.id}/cards`);
  for (const card of payload.cards) {
    const cardLeveling = levelingSystem.initializeSnippetLeveling();
    const title = (card.title || '').trim() || 'Untitled';
    await addDoc(userCardsRef, {
      title,
      description: card.description || '',
      solution: card.solution || '',
      language: card.language || payload.language || 'Other',
      tags: Array.isArray(card.tags) ? card.tags : ['github'],
      creationSource: 'github_import',
      githubPath: card.path || null,
      createdAt: timestamp,
      updatedAt: timestamp,
      level: cardLeveling.level,
      current_exp: cardLeveling.current_exp,
      exp_to_level: cardLeveling.exp_to_level,
    });
  }

  return { ok: true, deckId: deckRef.id, newDeckId: deckRef.id, cardCount: payload.cards.length };
}

export function messageForGitHubImportError(err) {
  const code = err?.code || '';
  const message = err?.message || '';

  if (code === 'functions/permission-denied' || /Pro feature/i.test(message)) {
    return { type: 'upgrade', text: 'GitHub Import is a Pro feature.' };
  }
  if (code === 'functions/unauthenticated') {
    return { type: 'auth', text: 'Sign in to import from GitHub.' };
  }
  if (code === 'functions/not-found') {
    return { type: 'error', text: 'Repository not found or is private.' };
  }
  if (code === 'functions/resource-exhausted') {
    return { type: 'error', text: 'GitHub rate limit reached. Try again later.' };
  }
  if (code === 'functions/failed-precondition') {
    return { type: 'error', text: message.replace(/^FirebaseError:\s*/i, '').replace(/^.*?:\s*/, '') || 'No importable source files found.' };
  }
  if (code === 'functions/invalid-argument') {
    return { type: 'error', text: message.replace(/^FirebaseError:\s*/i, '').replace(/^.*?:\s*/, '') || 'Invalid repository URL.' };
  }
  return { type: 'error', text: 'Could not import from GitHub. Try again.' };
}
